import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import {
  resetPassword,
  validateResetPasswordToken,
} from '../../lib/flotiq-client/auth-requests';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import ChangePasswordForm from '../../form/ChangePasswordForm/ChangePasswordForm';
import Authentication from '../../hoc/Authentication/Authentication';

const testId = 'change-password';

const ChangePasswordPage = () => {
  const [searchParams] = useSearchParams();
  const activationToken = searchParams.get('activationToken');
  const resetPasswordToken = searchParams.get('resetPasswordToken');
  const mode = searchParams.get('mode');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['validateResetPasswordToken'],
    queryFn: () => validateResetPasswordToken(resetPasswordToken ?? ''),
    enabled: !!resetPasswordToken,
    retry: false,
  });

  const isTokenValid = !!resetPasswordToken && !isLoading && !!data?.ok;

  const handlePasswordChange = useCallback(
    async (plainPassword: string) => {
      try {
        const { body, status, ok } = await resetPassword({
          plainPassword,
          plainPasswordRepeat: plainPassword,
          activationToken,
          resetPasswordToken,
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Form.FormChangePasswordSuccess'), {
          duration: 10000,
        });
        navigate('/');
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return { general: t('Form.CommunicationErrorMessage') };
        }
        const message = error.message.includes('Invalid reset password token.')
          ? t('Form.InvalidResetPasswordToken')
          : error.message || t('Form.ValidationError');
        toast.error(message);
        return {
          general: t('Form.ValidationError'),
          ...error.errors,
        };
      }
    },
    [activationToken, navigate, resetPasswordToken, t],
  );

  if (isLoading) return null;

  if (!isTokenValid) {
    return (
      <>
        <title>{t('Form.FormChangePasswordHeader')}</title>
        <Authentication
          paragraphText={t('Form.InvalidResetPasswordToken')}
          {...getTestProps(testId, 'authentication', 'testId')}
        ></Authentication>
      </>
    );
  }

  return (
    <>
      <title>{t('Form.FormChangePasswordHeader')}</title>
      <Authentication
        headerText={t('Form.FormChangePasswordHeader')}
        paragraphText={
          mode !== 'setPassword'
            ? t('Form.FormChangePasswordEnterNewPassword')
            : t('Form.FormChangePasswordSetPassword')
        }
        {...getTestProps(testId, 'authentication', 'testId')}
      >
        <ChangePasswordForm
          onSubmit={handlePasswordChange}
          {...getTestProps(testId, 'form', 'testId')}
        />
      </Authentication>
    </>
  );
};

export default ChangePasswordPage;
